import { useSyncExternalStore } from "react";

// Release 1.6.0: switches for features that ship dark and are turned on by a governed setting (the server is the
// authority and answers 404 while a switch is off). The SPA learns each switch after sign-in and hides the feature's
// menu entry until it is on. Default: off. Shared, so other 1.6.0 features can add their own key.

export type FeatureSwitchKey = "planning.calendar" | (string & {});

const state = new Map<string, boolean>();
const listeners = new Set<() => void>();
let version = 0;

export function setFeatureSwitch(key: FeatureSwitchKey, on: boolean): void {
  if ((state.get(key) ?? false) === on) return;
  state.set(key, on);
  version += 1;
  listeners.forEach((l) => l());
}

export function isFeatureOn(key: FeatureSwitchKey): boolean {
  return state.get(key) ?? false;
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

/** Re-renders the caller whenever any switch changes (the menu uses it). */
export function useFeatureSwitchesVersion(): number {
  return useSyncExternalStore(subscribe, () => version, () => version);
}

/** One switch, reactive. */
export function useFeatureSwitch(key: FeatureSwitchKey): boolean {
  return useSyncExternalStore(subscribe, () => isFeatureOn(key), () => isFeatureOn(key));
}

/** Tests only: every switch back to off. */
export function resetFeatureSwitchesForTests(): void {
  state.clear();
  version += 1;
  listeners.forEach((l) => l());
}
